/**
 * Die gemeinsame Schale aller neun Auftraggeber-Bildschirme.
 *
 * Sie trägt genau eine Zusage: **kein Bildschirm unter `/t/:id` wird ohne Sitzung gerendert.**
 * Vorher stand der Zugangsschutz in jeder Bildschirmdatei einzeln — mit dem Ergebnis, dass
 * `einstellungen.tsx` ihn als einzige nicht hatte, `useSitzung()` dort außerhalb des Schutzes
 * aufrief und der Bildschirm bei jedem Aufruf abstürzte: Stammdaten, Team, Webhook, Audit-Log und
 * die Verfahrensdokumentation waren in der ausgelieferten Anwendung nicht erreichbar. Eine Zusage,
 * die neunmal wiederholt werden muss, wird irgendwann achtmal wiederholt. Hier steht sie einmal.
 *
 * Dazu die zweite Frage, die vorher niemand beantwortete: Was, wenn die Adresse einen Auftraggeber
 * nennt, bei dem diese Sitzung gar nicht Mitglied ist — ein alter Link, ein entzogener Zugang,
 * eine falsch getippte Kennung? Dann antwortet jeder einzelne Aufruf dieses Bildschirms mit 403,
 * und der Nutzer liest neunmal dieselbe technische Meldung. Hier steht stattdessen ein Satz und
 * die Liste der Auftraggeber, die er tatsächlich hat.
 */
import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { RequireSession, useSitzung } from '../../../auth'
import { Verweis } from '../../../components/Verweis'

export const Route = createFileRoute('/t/$tenantId')({
  component: TenantSchale,
})

function TenantSchale(): ReactElement {
  return (
    <RequireSession>
      <Mitgliedschaft />
    </RequireSession>
  )
}

function Mitgliedschaft(): ReactElement {
  const { tenantId } = Route.useParams()
  const sitzung = useSitzung()
  const navigate = useNavigate()

  const mitglied = sitzung.tenants.some((tenant) => tenant.id === tenantId)
  if (mitglied) return <Outlet />

  return (
    <div className="seite">
      <h1>Kein Zugang zu diesem Auftraggeber</h1>
      <p role="alert">
        Diese Adresse gehört zu einem Auftraggeber, bei dem Sie nicht als Mitglied geführt werden.
        Prüfen Sie den Link, oder lassen Sie sich von einem Owner dieses Auftraggebers einladen.
      </p>
      {sitzung.tenants.length > 0 && (
        <>
          <p>Ihre Auftraggeber:</p>
          <ul>
            {sitzung.tenants.map((tenant) => (
              <li key={tenant.id}>
                <Verweis
                  href={`/t/${encodeURIComponent(tenant.id)}/dashboard`}
                  gehZu={(pfad) => {
                    void navigate({ to: pfad })
                  }}
                >
                  {tenant.name}
                </Verweis>
              </li>
            ))}
          </ul>
        </>
      )}
      {sitzung.contractors.length > 0 && (
        <p>
          <Verweis
            href="/me/provisionen"
            gehZu={(pfad) => {
              void navigate({ to: pfad })
            }}
          >
            Zu meinen Provisionen
          </Verweis>
        </p>
      )}
    </div>
  )
}
